import { apiFetch } from "@/integrations/backend/client";
import type { OnboardingData } from "./onboarding";

export type AdminUser = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  role: string | null;
  status: string | null;
  last_sign_in_at: string | null;
  created_at: string | null;
  bio: string | null;
  onboarding_completed: boolean | null;
  last_payment_at: string | null;
};

export type UpdateUserInput = {
  first_name?: string | null;
  last_name?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  role?: "admin" | "trainer" | "client";
};

export async function listUsers(): Promise<AdminUser[]> {
  const res = await apiFetch("/api/v1/admin/users", { method: "GET" });
  if (!res.ok) throw new Error(`users_list_failed:${res.status}`);
  return (await res.json()) as AdminUser[];
}

export async function updateUser(userId: string, updates: UpdateUserInput): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/users/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`user_update_failed:${res.status}`);
}

export async function deactivateUser(userId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/users/${userId}/deactivate`, { method: "POST" });
  if (!res.ok) throw new Error(`user_deactivate_failed:${res.status}`);
}

export async function reactivateUser(userId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/users/${userId}/reactivate`, { method: "POST" });
  if (!res.ok) throw new Error(`user_reactivate_failed:${res.status}`);
}

export async function inviteUser(email: string, role: "admin" | "trainer" | "client"): Promise<void> {
  const res = await apiFetch("/api/v1/admin/invite-user", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) throw new Error(`user_invite_failed:${res.status}`);
}

/**
 * Upload avatar for the current user (admin).
 * Note: The backend endpoint only supports uploading avatars for the current authenticated user.
 * The returned URL can then be saved to another user's profile via updateUser().
 */
export async function uploadAvatar(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiFetch("/api/v1/profiles/me/avatar", {
    method: "POST",
    body: form,
  });
  if (!res.ok) throw new Error(`avatar_upload_failed:${res.status}`);
  const json = await res.json();
  return json.url as string;
}

export type AdminClient = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  goals: string[];
  pricing_tier: string | null;
  last_payment_date: string | null;
  trainer: AdminTrainer | null;
  // Keep these for compatibility (points and last workout - dummy for now)
  workoutsThisMonth: number;
  totalWorkouts: number;
  pointsEarned: number;
  lastWorkoutDate: string | null;
  lastWorkoutType: string | null;
};

export type AdminTrainer = {
  id: string;
  name: string;
  initials: string;
  avatarUrl: string;
};

export async function listClients(): Promise<AdminClient[]> {
  const res = await apiFetch("/api/v1/admin/clients", { method: "GET" });
  if (!res.ok) throw new Error(`clients_list_failed:${res.status}`);
  return (await res.json()) as AdminClient[];
}

export async function listTrainers(): Promise<AdminTrainer[]> {
  const res = await apiFetch("/api/v1/admin/trainers", { method: "GET" });
  if (!res.ok) throw new Error(`trainers_list_failed:${res.status}`);
  return (await res.json()) as AdminTrainer[];
}

export async function assignTrainer(clientId: string, trainerId: string | null): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/clients/${clientId}/assign-trainer`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ trainer_id: trainerId }),
  });
  if (!res.ok) throw new Error(`assign_trainer_failed:${res.status}`);
}

export type ClientProfileData = {
  profile: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  };
  onboarding: OnboardingData;
};

export async function getClientProfile(clientId: string): Promise<ClientProfileData> {
  const res = await apiFetch(`/api/v1/admin/clients/${clientId}/profile`, {
    method: "GET",
  });
  if (!res.ok) throw new Error(`get_client_profile_failed:${res.status}`);
  return (await res.json()) as ClientProfileData;
}

export type UpdateClientOnboardingInput = {
  // Admin can update any onboarding fields (same shape as `/api/v1/onboarding/me`)
  // This endpoint is partial-update (PATCH) so all fields are optional.
  // Note: avoid sending `id` from the client; backend ignores unknown keys anyway.
} & Partial<OnboardingData>;

export async function updateClientOnboarding(
  clientId: string,
  updates: UpdateClientOnboardingInput
): Promise<OnboardingData> {
  const res = await apiFetch(`/api/v1/admin/clients/${clientId}/onboarding`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`update_client_onboarding_failed:${res.status}`);
  return (await res.json()) as OnboardingData;
}

